/**
 * Helpers puros de mapeo entre Google Maps (Places API New / BFF) y el formato legacy
 * usado por los consumidores del panel (`place_id`, `description`, `structured_formatting`,
 * `address_components`).
 *
 * Este módulo NO importa hooks de React ni efectos de Google Maps a propósito: permite
 * tests unitarios aislados (sin mockear React, sin window.google) y reduce el grafo de
 * dependencias de quienes solo necesitan transformar payloads.
 */

/**
 * Convierte componentRestrictions legacy a includedRegionCodes (Places API New / BFF).
 * @param {Object|undefined} componentRestrictions - { country: 'AR' }
 * @returns {string[]|undefined} - códigos ISO alpha-2 en minúsculas (p. ej. ['ar']) o undefined
 */
export function toIncludedRegionCodes(componentRestrictions) {
  if (!componentRestrictions?.country) return undefined;
  const code = componentRestrictions.country;
  return [typeof code === 'string' ? code.toLowerCase() : String(code).toLowerCase()];
}

/**
 * Misma lista en mayúsculas para APIs que documentan ISO alpha-2 en upper-case.
 * @param {Object|undefined} componentRestrictions
 * @returns {string[]|undefined}
 */
export function toIncludedRegionCodesUpper(componentRestrictions) {
  const lower = toIncludedRegionCodes(componentRestrictions);
  return lower?.map(c => c.toUpperCase());
}

/**
 * Convierte addressComponents (New) a address_components (legacy) para compatibilidad.
 * @param {Array|undefined} addressComponents - Array de AddressComponent (longText, shortText, types)
 * @returns {Array} - Array con long_name, short_name, types
 */
export function toLegacyAddressComponents(addressComponents) {
  if (!Array.isArray(addressComponents)) return [];
  return addressComponents.map(ac => ({
    long_name: ac.longText ?? ac.long_name ?? '',
    short_name: ac.shortText ?? ac.short_name ?? '',
    types: ac.types ?? []
  }));
}

/**
 * Mapea PlacePrediction (New / BFF) a formato legacy para compatibilidad con consumidores.
 * @param {Object} placePrediction - PlacePrediction de la API New o respuesta alineada del BFF
 * @returns {Object|null} - { place_id, description, structured_formatting } o null si la entrada es inválida
 */
export function mapPlacePredictionToLegacy(placePrediction) {
  if (!placePrediction) return null;
  const text = placePrediction.text;
  const mainText = placePrediction.mainText;
  const secondaryText = placePrediction.secondaryText;
  const textStr = typeof text?.text === 'string' ? text.text : text ? String(text) : '';
  const mainStr = typeof mainText?.text === 'string' ? mainText.text : mainText ? String(mainText) : '';
  const secondaryStr =
    typeof secondaryText?.text === 'string' ? secondaryText.text : secondaryText ? String(secondaryText) : '';
  return {
    place_id: placePrediction.placeId ?? placePrediction.place_id,
    description: textStr,
    structured_formatting: {
      main_text: mainStr || textStr,
      secondary_text: secondaryStr
    }
  };
}
